import React, { useEffect, useMemo, useRef } from "react";
import { Alert, Box, Grid, Paper, Typography } from "@mui/material";
import { BooleanInput, Create, FormDataConsumer, SimpleForm, TextInput, useGetList } from "react-admin";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFormContext, useWatch } from "react-hook-form";
import { RichTextInput, RichTextInputToolbar } from "ra-input-rich-text";
import CustomSelect from "../../../ui/custom-select";
import { ContentPageSX } from "../../../styled/custom";
import { filesSize, hasValue } from "../../../utils/schemas";
import CustomTextField from "../../../ui/custom-text-field";
import FileUploadInput from "../../../ui/custom-file-update-input";
import MediaUploadProgressOverlay from "../../../ui/media-upload-progress-overlay";
import ThumbnailUploadInput from "../../../ui/thumbnail-upload-input";
import { MEDIA_ACCEPT_BY_CLASS, MEDIA_CLASSES, MEDIA_MAXIMUM_FILES_SIZE } from "./common/constants";
import {
  getEligibleTypePlanIds,
  normalizeIds,
  renderTypePlanDialogExtra,
  renderTypePlanItem,
  withPlanAvailability,
} from "./common/type-plan-utils";

const sectionSx = {
  p: 2,
  borderRadius: "10px",
  border: "1px solid rgba(0,0,0,0.09)",
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)",
  height: "100%",
};

const sectionTitleSx = {
  fontSize: "0.95rem",
  fontWeight: 700,
  color: "#2f2f2f",
  mb: 1.2,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const Schema = Yup.object({
  media_class: hasValue("Selecione uma classe"),
  category_media_id: hasValue("Selecione uma categoria"),
  files: Yup.mixed().when("media_class", {
    is: (media_class) => ["Audio", "Video"].includes(media_class),
    then: filesSize(
      MEDIA_MAXIMUM_FILES_SIZE,
      "Selecione 1 ou mais arquivos com tamanho maximo de 2 GB no total"
    ),
    otherwise: Yup.mixed().notRequired(),
  }),
  name: Yup.string().when("media_class", {
    is: "Texto",
    then: Yup.string().required("O titulo e obrigatorio para classe Texto"),
    otherwise: Yup.string().notRequired(),
  }),
  body: Yup.string().trim().when("media_class", {
    is: "Texto",
    then: Yup.string().trim().nullable().required("O corpo e obrigatorio para classe Texto"),
    otherwise: Yup.string().notRequired(),
  }),
  thumbnail_file: Yup.mixed().notRequired(),
  expires_at: Yup.string()
    .nullable()
    .test("valid-date", "Data de expiração inválida", (value) => {
      if (!value) return true;
      return !Number.isNaN(new Date(value).getTime());
    })
    .test("future-plus-1-day", "A data de expiração deve ser no mínimo 1 dia no futuro", (value) => {
      if (!value) return true;
      const selected = new Date(value);
      const minDate = new Date(Date.now() + 24 * 60 * 60 * 1000);
      return selected.getTime() >= minDate.getTime();
    }),
});

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const AutoPlanSelector = ({ typePlans }) => {
  const { setValue } = useFormContext();
  const categoryMediaId = useWatch({ name: "category_media_id" });
  const mediaClass = useWatch({ name: "media_class" });
  const selectedTypePlans = useWatch({ name: "type_plan_ids" });
  const lastAutoIdsRef = useRef([]);

  const eligibleTypePlanIds = useMemo(
    () => getEligibleTypePlanIds(typePlans, categoryMediaId, mediaClass),
    [typePlans, categoryMediaId, mediaClass]
  );

  useEffect(() => {
    const normalizedSelected = normalizeIds(selectedTypePlans);
    const lastAutoIds = lastAutoIdsRef.current;
    const eligibleSet = new Set(eligibleTypePlanIds);
    const sanitizedSelected = normalizedSelected.filter((typePlanId) => eligibleSet.has(typePlanId));
    const hasManualSelection =
      normalizedSelected.length > 0 &&
      JSON.stringify(normalizedSelected) !== JSON.stringify(lastAutoIds);

    if (normalizedSelected.length !== sanitizedSelected.length) {
      setValue("type_plan_ids", sanitizedSelected, { shouldDirty: true });
      return;
    }

    if (!hasManualSelection) {
      setValue("type_plan_ids", eligibleTypePlanIds, { shouldDirty: false });
      lastAutoIdsRef.current = eligibleTypePlanIds;
    }
  }, [eligibleTypePlanIds, selectedTypePlans, setValue]);

  return null;
};

const MediaCreate = () => {
  const { data: categoriesData, isLoading: isLoadingCategories } = useGetList("category-media", {
    pagination: { page: 1, perPage: 1000000 },
    filter: { activated: true },
  });
  const { data: typePlansData, isLoading: isLoadingTypePlans } = useGetList("type-plan", {
    pagination: { page: 1, perPage: 1000000 },
    filter: { activated: true },
  });

  const transformMediaPayload = (formData) => {
    const eligibleTypePlanIds = getEligibleTypePlanIds(
      typePlansData || [],
      formData.category_media_id,
      formData.media_class
    );
    const eligibleSet = new Set(eligibleTypePlanIds);

    return {
      ...formData,
      type_plan_ids: JSON.stringify(
        normalizeIds(formData.type_plan_ids).filter((typePlanId) => eligibleSet.has(typePlanId))
      ),
    };
  };

  return (
    <Create title="Novo Produto da Cesta" redirect="list" sx={ContentPageSX}>
      <>
        <MediaUploadProgressOverlay />
        <SimpleForm
          noValidate
          resolver={yupResolver(Schema)}
          transform={transformMediaPayload}
          // sx={{ maxWidth: "1180px", mx: "auto", width: "100%" }}
        >
          <AutoPlanSelector typePlans={typePlansData || []} />
          <FormDataConsumer>
            {({ formData }) => {
              const isHypermedia = formData.media_class !== "Texto";
              const fileAccept =
                MEDIA_ACCEPT_BY_CLASS[formData.media_class] ?? MEDIA_ACCEPT_BY_CLASS.Audio;
              const plansWithAvailability = withPlanAvailability(
                typePlansData || [],
                formData.media_class,
                formData.category_media_id
              );

              return (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Paper sx={sectionSx}>
                    <Typography sx={sectionTitleSx}>Dados Gerais</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <CustomSelect
                          data={MEDIA_CLASSES}
                          name="media_class"
                          label="Classe"
                          multiple={false}
                          fullWidth
                          isRequired
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        {!isLoadingCategories && (
                          <CustomSelect
                            data={categoriesData}
                            name="category_media_id"
                            label="Categoria"
                            multiple={false}
                            fullWidth
                            isRequired
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={12}>
                        {!isLoadingTypePlans && (
                          <CustomSelect
                            data={plansWithAvailability}
                            name="type_plan_ids"
                            label="Planos Disponiveis"
                            multiple={true}
                            fullWidth
                            optionDisabled="disabled"
                            renderItem={renderTypePlanItem}
                            renderDialogExtra={renderTypePlanDialogExtra}
                          />
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextInput
                          source="expires_at"
                          label="Expira em"
                          fullWidth
                          type="datetime-local"
                          format={toDateTimeLocalValue}
                          parse={(value) => (value ? value : null)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Paper sx={sectionSx}>
                        <Typography sx={sectionTitleSx}>Conteudo</Typography>
                        {!isHypermedia && (
                          <>
                            <CustomTextField name="name" label="Titulo" fullWidth />
                            <RichTextInput
                              fullWidth
                              source="body"
                              label="Corpo"
                              toolbar={<RichTextInputToolbar size="medium" />}
                            />
                          </>
                        )}
                        {isHypermedia && (
                          <>
                            <FileUploadInput
                              source="files"
                              sourceDescription="descriptions"
                              label="Tamanho Maximo dos Arquivos Somados: 2 GB"
                              multiple={true}
                              maxSize={MEDIA_MAXIMUM_FILES_SIZE}
                              accept={fileAccept}
                            />
                            {formData.media_class === "Video" && (
                              <Alert severity="info" sx={{ mt: 1 }}>
                                Para gerar thumbnail por frame automaticamente, envie apenas 1 video.
                              </Alert>
                            )}
                          </>
                        )}
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Paper sx={sectionSx}>
                        <Typography sx={sectionTitleSx}>Thumbnail</Typography>
                        <ThumbnailUploadInput
                          source="thumbnail_file"
                          nameValue={formData.name}
                          thumbnailUrlValue={formData.thumbnail_url}
                        />
                        {formData.media_class === "Video" && (
                          <BooleanInput
                            source="thumbnail_from_video_frame"
                            label="Gerar thumbnail do primeiro frame (quando nenhuma imagem for enviada)"
                          />
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              );
            }}
          </FormDataConsumer>
        </SimpleForm>
      </>
    </Create>
  );
};

export default MediaCreate;
